import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderKanban, Plus, Pencil, Trash2, Lock, Shield, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsManagement({ user }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete project "${name}"? Items will be unassigned.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const canEdit = user?.role !== 'viewer';
  const isAdmin = user?.role === 'admin';

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FolderKanban className="w-8 h-8 text-primary" />
            <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">Projects</h1>
          </div>
          <p className="text-text-secondary">Group credentials and certificates by project</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditProject(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="btn-new-project"
          >
            <Plus size={18} /> New Project
          </button>
        )}
      </div>

      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((proj) => (
          <div
            key={proj.id}
            className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-primary/50 transition-colors"
            data-testid={`project-card-${proj.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-primary/10 p-2.5 rounded-lg">
                <FolderKanban className="w-5 h-5 text-primary" />
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditProject(proj); setShowModal(true); }}
                    className="p-1.5 text-text-muted hover:text-primary transition-colors"
                    data-testid={`btn-edit-proj-${proj.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(proj.id, proj.name)}
                      className="p-1.5 text-text-muted hover:text-accent transition-colors"
                      data-testid={`btn-delete-proj-${proj.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <h3 className="font-semibold text-text-primary mb-1">{proj.name}</h3>
            {proj.description && <p className="text-text-muted text-sm mb-3 line-clamp-2">{proj.description}</p>}
            <div className="flex gap-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-text-muted text-sm">
                <Lock size={14} />
                <span>{proj.credential_count} creds</span>
              </div>
              <div className="flex items-center gap-1.5 text-text-muted text-sm">
                <Shield size={14} />
                <span>{proj.certificate_count} certs</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && projects.length === 0 && (
        <div className="text-center py-16">
          <FolderKanban className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-text-primary font-semibold text-lg mb-1">No projects yet</h3>
          <p className="text-text-muted text-sm">Create a project to organize credentials and certificates</p>
        </div>
      )}

      {showModal && (
        <ProjectModal
          project={editProject}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchProjects(); }}
        />
      )}
    </div>
  );
}

function ProjectModal({ project, onClose, onSuccess }) {
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (project) {
        await axios.put(`${API}/projects/${project.id}`, { name: name.trim(), description }, { headers });
      } else {
        await axios.post(`${API}/projects`, { name: name.trim(), description }, { headers });
      }
      toast.success(project ? 'Project updated' : 'Project created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-modal">
      <div className="bg-white w-full max-w-md rounded-xl border-2 border-gray-200 shadow-xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="font-manrope font-bold text-xl text-text-primary">{project ? 'Edit Project' : 'New Project'}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Project Name *</label>
            <input
              type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
              placeholder="e.g., Production, Staging, Mobile App"
              data-testid="input-project-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Description</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
              placeholder="Optional description..."
              data-testid="input-project-desc"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="btn-save-project">
              {loading ? 'Saving...' : project ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={onClose} className="btn-outline flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
